import { Quote, Linkedin } from "lucide-react";
import testimonialZimmer from "@/assets/testimonial-zimmer.jpg";
import testimonialKazlauskas from "@/assets/testimonial-kazlauskas.jpg";
import testimonialSarah from "@/assets/testimonial-sarah.jpg";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

interface TestimonialsProps {
  language: 'en' | 'de';
}

export const Testimonials = ({ language }: TestimonialsProps) => {
  const content = {
    en: {
      title: "Trusted by Leaders",
      subtitle: "What executives say about working with me",
      testimonials: [
        {
          name: "Dr. Maximilian Zimmer",
          role: "Chief Technology Officer",
          company: "Reishauer AG",
          country: "Switzerland",
          linkedIn: "https://www.linkedin.com/in/maximilian-zimmer-8949121b0/",
          quote: "Stefan Kaiser's strategic guidance is supporting our digital transformation journey. His deep expertise in both technology and business strategy helped us navigate complex challenges and deliver measurable results. A true sparring partner who challenges your thinking while providing practical solutions.",
          image: testimonialZimmer
        },
        {
          name: "Žilvinas Kazlauskas",
          role: "Former Chief Execution Officer & Investor",
          company: "DocLogix UAB & LitBAN",
          country: "Lithuania",
          linkedIn: "https://www.linkedin.com/in/zilvinaskazlauskas/",
          quote: "Working with Stefan Kaiser is a game-changer for our organization. His ability to bridge technical complexity with business outcomes is exceptional. He doesn't just provide advice – he becomes a trusted partner who truly understands your challenges and helps you achieve breakthrough results.",
          image: testimonialKazlauskas
        },
        {
          name: "Sarah Mitchell",
          role: "VP Digital Transformation",
          company: "TechVision Industries",
          quote: "Stefan's expertise in AI and business model innovation helped us reimagine our entire value proposition. His consulting approach is both strategic and highly practical. The insights gained during our collaboration continue to drive value long after the engagement.",
          image: testimonialSarah
        }
      ]
    },
    de: {
      title: "Vertrauen von Führungskräften",
      subtitle: "Was Führungskräfte über die Zusammenarbeit sagen",
      testimonials: [
        {
          name: "Dr. Maximilian Zimmer",
          role: "Chief Technology Officer",
          company: "Reishauer AG",
          country: "Schweiz",
          linkedIn: "https://www.linkedin.com/in/maximilian-zimmer-8949121b0/",
          quote: "Stefan Kaisers strategische Beratung unterstützt unsere digitale Transformationsreise. Seine tiefe Expertise in Technologie und Geschäftsstrategie half uns, komplexe Herausforderungen zu meistern und messbare Ergebnisse zu erzielen. Ein echter Sparringspartner, der Ihr Denken hinterfragt und gleichzeitig praktische Lösungen bietet.",
          image: testimonialZimmer
        },
        {
          name: "Žilvinas Kazlauskas",
          role: "Ehemaliger Chief Execution Officer & Investor",
          company: "DocLogix UAB & LitBAN",
          country: "Litauen",
          linkedIn: "https://www.linkedin.com/in/zilvinaskazlauskas/",
          quote: "Die Zusammenarbeit mit Stefan Kaiser ist ein Wendepunkt für unser Unternehmen. Seine Fähigkeit, technische Komplexität mit Geschäftsergebnissen zu verbinden, ist außergewöhnlich. Er gibt nicht nur Ratschläge – er wird ein vertrauenswürdiger Partner, der Ihre Herausforderungen wirklich versteht und Ihnen hilft, Durchbruchsergebnisse zu erzielen.",
          image: testimonialKazlauskas
        },
        {
          name: "Sarah Mitchell",
          role: "VP Digital Transformation",
          company: "TechVision Industries",
          quote: "Stefans Expertise in KI und Geschäftsmodellinnovation half uns, unser gesamtes Wertversprechen neu zu denken. Sein Beratungsansatz ist sowohl strategisch als auch hochpraktisch. Die gewonnenen Erkenntnisse schaffen noch lange nach dem Engagement Mehrwert.",
          image: testimonialSarah
        }
      ]
    }
  };

  const t = content[language];

  return (
    <section id="testimonials" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">{t.title}</h2>
          <p className="text-xl text-muted-foreground">{t.subtitle}</p>
        </div>

        <div className="max-w-5xl mx-auto px-12">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent>
              {t.testimonials.map((testimonial, index) => (
                <CarouselItem key={index}>
                  <div className="bg-card rounded-2xl shadow-elegant p-8 md:p-12 border border-border">
                    <Quote className="h-12 w-12 text-accent mb-6" />
                    
                    <blockquote className="text-lg md:text-xl text-foreground/90 mb-8 leading-relaxed italic">
                      "{testimonial.quote}"
                    </blockquote>

                    <div className="flex items-center gap-4">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-accent shadow-glow"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-lg">{testimonial.name}</div>
                        <div className="text-muted-foreground">{testimonial.role}</div>
                        <div className="text-sm text-muted-foreground">{testimonial.company}</div>
                        {testimonial.country && (
                          <div className="text-sm text-muted-foreground">{testimonial.country}</div>
                        )}
                      </div>
                      {testimonial.linkedIn && (
                        <a
                          href={testimonial.linkedIn}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent hover:text-accent/80 transition-smooth"
                          aria-label="LinkedIn Profile"
                        >
                          <Linkedin className="h-5 w-5" />
                        </a>
                      )}
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </div>
    </section>
  );
};
